import * as React from 'react';
import { useEffect, useReducer, useState } from 'react';
import { RootState } from '../reducers';
import { connect, useSelector } from 'react-redux';
import { ReactComponent as NavigateBack } from '../assets/navigate_back.svg';
import {
  navigateBackRecoverEmailAccounts,
  recoverEmailAccounts,
} from '../actions/orderActions';
import { bindActionCreators } from 'redux';
import { getDomainType, isRoleTitan } from './utils';
import ConfirmationModal from './ConfirmationModal';
import { ReactComponent as Alert } from '../assets/action_history_alert.svg';
import {
  RecoverableAccount,
  RecoverableOrder,
  RecoverAccountsInfo,
} from '../model/RecoverAccountsInfo';
import { startRecoveryTaskSync } from '../service/recoveryTaskSyncService';
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from 'react-intl';
import {
  bundleDetailsSelector,
  orderDetailsSelector,
} from '../selectors/bundleSelectors';
import { getLatestMailOrder } from '../helpers/product.helpers';
import {
  authTokenSelector,
  userRoleSelector,
} from '../selectors/userSelectors';

type Props = {
  recoverAccountsInfo: RecoverAccountsInfo;
  recoverableOrders: RecoverableOrder[];
  recoverEmailAccounts: (
    requestBody: object,
    domainName: string,
    token: string,
    region: string
  ) => void;
  navigateBackRecoverEmailAccounts: () => void;
} & WrappedComponentProps;

const RecoverEmailAccounts: React.FC<Props> = ({
  recoverAccountsInfo,
  recoverableOrders,
  recoverEmailAccounts,
  navigateBackRecoverEmailAccounts,
  intl,
}) => {
  // dummy state to force a re-render on our mutating toggles
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const {
    partner: { name: partnerName },
    customer: { id: customerId, email: customerEmail },
    domainName,
  } = useSelector(bundleDetailsSelector);
  const orderDetails = useSelector(orderDetailsSelector);
  const token = useSelector(authTokenSelector);
  const userRole = useSelector(userRoleSelector);

  // mimic constructor’s initial “isChecked = false” setup
  useEffect(() => {
    recoverableOrders.forEach((domain) => {
      domain.isChecked = false;
      domain.accountDetails.forEach((acct) => {
        acct.isChecked = false;
      });
    });
    // only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDomainName = () => domainName;
  const getMailOrderId = () => {
    const mailOrder = getLatestMailOrder(orderDetails);
    return mailOrder?.id;
  };

  const toggleSelected = (item: RecoverableAccount) => {
    item.isChecked = !item.isChecked;
    forceUpdate();
  };

  const toggleAll = (domain: RecoverableOrder) => {
    const newValue = !domain.isChecked;
    domain.isChecked = newValue;
    domain.accountDetails.forEach((acct) => {
      if (acct.isRecoverable) {
        acct.isChecked = newValue;
      }
    });
    forceUpdate();
  };

  const handleSubmit = () => {
    const selectedIds = recoverableOrders.flatMap((domain) =>
      domain.accountDetails
        .filter((acct) => acct.isChecked)
        .map((acct) => acct.id)
    );
    if (selectedIds.length > 0) {
      recoverEmailAccounts(
        { domainId: getMailOrderId(), accounts: selectedIds },
        getDomainName(),
        token,
        ''
      );
    }
  };

  const closeModalOnSuccess = () => {
    setShowConfirmationModal(false);
    startRecoveryTaskSync(getDomainName(), getMailOrderId(), token, '');
    navigateBackRecoverEmailAccounts();
  };

  const isOrderRecoveryDisabled = (accounts: RecoverableAccount[]) =>
    accounts.every((acct) => !acct.isRecoverable);

  const compareStorageUsed = (a: RecoverableAccount, b: RecoverableAccount) =>
    b.storageUsedInMB - a.storageUsedInMB;

  const disableRecoverButton = () => {
    return recoverableOrders
      .flatMap((d) => d.accountDetails.map((a) => a.isChecked))
      .every((checked) => !checked);
  };

  const getAccountsList = (accountDetails: RecoverableAccount[]) => {
    const withActive = accountDetails
      .filter((a) => a.isRecoverable)
      .sort(compareStorageUsed);
    const withoutActive = accountDetails
      .filter((a) => !a.isRecoverable)
      .sort(compareStorageUsed);

    const rows: JSX.Element[] = [];

    withActive.forEach((acct) => {
      rows.push(
        <tr key={`active-${acct.id}`} className="account-details-row">
          <td>
            <input
              type="checkbox"
              checked={acct.isChecked}
              onClick={() => toggleSelected(acct)}
              className="account-checkbox"
            />
          </td>
          <td className="account-detail-column1">{acct.email}</td>
          <td className="account-detail-column2">
            {acct.storageUsedInMB} MB / {acct.storageLimitInMB} MB
          </td>
        </tr>
      );
    });

    if (withoutActive.length > 0) {
      rows.push(
        <tr key="alert-row">
          <td colSpan={3} className="recover-email-account-alert">
            <div className="recover-email-account-alert-container">
              <Alert />
              <span className="recover-email-account-alert-text">
                <FormattedMessage id="recover_email_account_alert_text" />
              </span>
            </div>
          </td>
        </tr>
      );
      withoutActive.forEach((acct) => {
        rows.push(
          <tr key={`inactive-${acct.id}`} className="account-details-row">
            <td>
              <input
                type="checkbox"
                checked={acct.isChecked}
                disabled
                onClick={() => toggleSelected(acct)}
                className="account-checkbox"
              />
            </td>
            <td className="account-detail-column1 disabled">{acct.email}</td>
            <td className="account-detail-column2 disabled">
              {acct.storageUsedInMB} MB / {acct.storageLimitInMB} MB
            </td>
          </tr>
        );
      });
    }

    return rows;
  };

  const getRecoverableAccounts = () =>
    recoverableOrders.map((domain) => {
      const header = `${domain.partnerName} | ${domain.source ? domain.source + '|' : ''} ${getDomainType(
        domain.domainType
      )} | ${domain.planType} | ${domain.customerEmail}`;
      return domain.accountDetails.length > 0 ? (
        <div key={domain.domainId} className="recoverable-accounts-container">
          <text className="accounts-details-header">{header}</text>
          <table className="account-details-table">
            <thead>
              <tr className="account-details-row">
                <th>
                  <input
                    type="checkbox"
                    checked={domain.isChecked}
                    disabled={isOrderRecoveryDisabled(domain.accountDetails)}
                    onClick={() => toggleAll(domain)}
                    className="account-checkbox"
                  />
                </th>
                <th className="account-detail-column1">
                  <FormattedMessage id="account_heading" />
                </th>
                <th className="account-detail-column2">
                  <FormattedMessage id="quota_header" />
                </th>
              </tr>
            </thead>
            <tbody>{getAccountsList(domain.accountDetails)}</tbody>
          </table>
        </div>
      ) : null;
    });

  const heading = <FormattedMessage id="recover_email_accounts_header" />;
  const mailOrder = getLatestMailOrder(orderDetails);
  const source =
    (mailOrder.source ? mailOrder.source + ' : ' : '') +
    getDomainType(mailOrder?.mailOrderDetails?.domainType) +
    ' : ' +
    customerId;
  const quota = mailOrder?.mailOrderDetails?.quota;
  const accountCountPerStatus =
    mailOrder?.mailOrderDetails?.accountCountPerStatus;
  const maxNoOfAccs = quota?.accountsLimit || 0;
  const noOfAccsCreated =
    accountCountPerStatus.active + accountCountPerStatus.suspended || 0;

  return (
    <div className="recover-accounts-container">
      <div className="recover-accounts-heading-container">
        <NavigateBack
          className="recover-accounts-navigate-back"
          onClick={navigateBackRecoverEmailAccounts}
        />
        <div className="recover-accounts-heading">
          <span className="recover-accounts-heading-text">{heading}</span>
        </div>
      </div>

      <table className="source-order-container">
        <thead>
          <tr className="account-details-header-row">
            {isRoleTitan(userRole) && (
              <th className="column-partner">
                <FormattedMessage id="recover-table-header-partner" />
              </th>
            )}
            <th className="column-source">
              <FormattedMessage id="recover-table-header-source" />
            </th>
            <th className="column-accounts">
              <FormattedMessage id="recover-table-header-accounts" />
            </th>
            <th className="column-plan">
              <FormattedMessage id="recover-table-header-plan" />
            </th>
            <th className="column-customer-email">
              <FormattedMessage id="recover-table-header-customer-email" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="source-order-row">
            {isRoleTitan(userRole) && (
              <td className="column-partner">{partnerName}</td>
            )}
            <td className="column-source">{source}</td>
            <td className="column-accounts">
              {noOfAccsCreated} / {maxNoOfAccs}
            </td>
            {/*<td className="column-plan">{domain.plan.type}</td>*/}
            {/*<td className="column-customer-email">{domain.customerEmail}</td>*/}
            <td className="column-plan">{mailOrder?.plan?.planType}</td>
            <td className="column-plan">{customerEmail}</td>
          </tr>
        </tbody>
      </table>

      <text className="accounts-list-header">
        <FormattedMessage id="select-emails-to-recover" />
      </text>

      <div className="account-details-container">
        <div className="account-details-table-container">
          {getRecoverableAccounts()}
        </div>
        <div className="recover-button-container">
          <button
            className="button button-primary recover-button"
            disabled={disableRecoverButton()}
            onClick={() => setShowConfirmationModal(true)}
          >
            <FormattedMessage id="recover-accounts" />
          </button>
        </div>
      </div>

      {showConfirmationModal && (
        <div className="modal">
          <div className="modal-content confirmation-modal">
            <ConfirmationModal
              heading={intl.formatMessage({ id: 'are-you-sure' })}
              description={intl.formatMessage({
                id: 'action-cannot-be-undone',
              })}
              positiveButtonText={intl.formatMessage({
                id: 'recover-accounts-positive-button-text',
              })}
              negativeButtonText={intl.formatMessage({ id: 'cancel' })}
              shouldShowLoader
              shouldShowSuccess={recoverAccountsInfo.recoverAccountsSuccess}
              requestFailed={recoverAccountsInfo.recoverAccountsError}
              isLoadingInProgress={
                recoverAccountsInfo.recoverAccountsInProgress
              }
              buttonLoadingText={intl.formatMessage({ id: 'requesting' })}
              buttonSuccessText={intl.formatMessage({ id: 'submitted' })}
              positiveButtonAction={handleSubmit}
              negativeButtonAction={() => setShowConfirmationModal(false)}
              successAction={closeModalOnSuccess}
              failureAction={() => setShowConfirmationModal(false)}
              closeAction={() => setShowConfirmationModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const orders = state.orders as any;
  return {
    recoverableOrders: orders.recoverEmailAccountState.recoverableOrders,
    recoverAccountsInfo: orders.recoverAccountsInfo,
  };
};

const mapDispatchToProps = (dispatch: any) =>
  bindActionCreators(
    { recoverEmailAccounts, navigateBackRecoverEmailAccounts },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(RecoverEmailAccounts));
